import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@interstate/components/Box';
import { Spinner } from '@interstate/components/Spinner';
import { Typography } from '@interstate/components/Typography';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vAutoSelectors from '@makemydeal/dr-dash-store/selectors/vAutoSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as vAutoActionCreators from '@makemydeal/dr-dash-store/actions/vAutoActionCreators';
import type { VAutoAppraisalMessageRejectionReason as VAutoAppraisalRejectionReason } from '@makemydeal/dr-dash-store/utils/vAutoAppraisalTelemetry';

import { getPostMessageTarget, toIntegratedAppraisalUrl } from './appraisalSlideOutUtils';
import type { SlideOutComponentProps } from '../../types';

// Cross-site message the iframed Integrated Appraisal page posts to the parent
// window when a save/finalize completes. On receipt we refetch the appraisal
// record and close the slide-out. See the vAuto Integrated Appraisal guide.
type VAutoAppraisalMessage = {
    action: 'Save' | 'Finalize';
    appraisalId: string;
};

const isVAutoAppraisalMessage = (data: unknown): data is VAutoAppraisalMessage => {
    const message = data as Partial<VAutoAppraisalMessage> | null;
    return !!message && (message.action === 'Save' || message.action === 'Finalize') && !!message.appraisalId;
};

// Action only, without the appraisalId the guard above also requires — so a trusted-but-incomplete
// callback can be reported rather than dropped in silence.
const namesVAutoAppraisalAction = (data: unknown): boolean => {
    const message = data as Partial<VAutoAppraisalMessage> | null;
    return !!message && (message.action === 'Save' || message.action === 'Finalize');
};
const VAUTO_APPRAISAL_SAVED_MESSAGE = 'Appraisal Saved';

// Per open panel, counted PER REASON. Capped because reporting turns a string compare into a dispatch a
// hostile frame could loop. Must stay per-reason: a shared budget would let spoofed `originMismatch`
// traffic permanently starve `missingAppraisalId`.
const MAX_REJECTED_MESSAGE_REPORTS = 3;

// Rendered only while the slide-out is open so the vAuto/config selectors it
// reads are never evaluated for a deal that has no config loaded (e.g. when the
// slide-out is mounted-but-closed inside BaseDealSlideOutProvider).
export const AppraisalSlideOutContent = ({ onClose }: { onClose: SlideOutComponentProps['onClose'] }) => {
    const dispatch = useDispatch();
    const [isIframeLoaded, setIsIframeLoaded] = useState(false);

    // The vAuto appraisal flow is scoped to a single VIN: the trade-in the appraisal was opened for.
    const tradeInIndex = useSelector(baseDealSelectors.getAppraisalTradeInIndex);

    const vin = useSelector(tradeInsSelectors.getVin(tradeInIndex));
    const vehicleMileage = useSelector(tradeInsSelectors.getTradeMileage(tradeInIndex));
    const isVAutoAppraisalEnabled = useSelector(dealerSelectors.isVAutoAppraisalEnabled);
    const useCoOrgIdInVAutoAppraisalUrl = useSelector(featureToggleSelectors.useCoOrgIdInVAutoAppraisalUrl);
    const commonOrgId = useSelector(offerReduxSelectors.getCommonOrgId);
    // Shared appraisal URL (legacy Default.aspx). We rewrite it to the integrated
    // page for the iframe via toIntegratedAppraisalUrl below.
    const vAutoAppraisalUrl = useSelector(configSelectors.getVAutoAppraisalUrl);
    const currAppraisal = useSelector(vAutoSelectors.getCurrentAppraisalByVin(vin));

    const appraisalUrl = useMemo(() => {
        if (!vAutoAppraisalUrl || !vin) {
            return '';
        }

        const appraisalPayload = JSON.stringify({ Vin: vin, Odometer: vehicleMileage });
        const integratedUrl = toIntegratedAppraisalUrl(vAutoAppraisalUrl);
        const baseUrl = `${integratedUrl}?${useCoOrgIdInVAutoAppraisalUrl ? `CommonOrgId=${commonOrgId}&` : ''}`;

        return currAppraisal
            ? `${baseUrl}Id=${currAppraisal.id}&AppraisalStatus=${currAppraisal.status}`
            : `${baseUrl}SelectedExitStrategy=Retail&InitialData=${appraisalPayload}`;
    }, [vAutoAppraisalUrl, vin, vehicleMileage, useCoOrgIdInVAutoAppraisalUrl, commonOrgId, currAppraisal]);

    const launchType = currAppraisal ? 'existing' : 'new';
    const iframeShownAtRef = useRef<number | undefined>(undefined);

    // Show the loader again whenever the appraisal URL changes.
    // Must stay a LAYOUT effect: a passive useEffect is scheduled after paint, so a mid-panel URL flip
    // lets the new document's load event beat it — mismeasuring iframeLoadMs or losing the load entirely.
    useLayoutEffect(() => {
        setIsIframeLoaded(false);
        iframeShownAtRef.current = appraisalUrl ? performance.now() : undefined;
    }, [appraisalUrl]);

    const handleIframeLoad = () => {
        setIsIframeLoaded(true);

        const shownAt = iframeShownAtRef.current;
        iframeShownAtRef.current = undefined;

        if (typeof shownAt === 'number') {
            dispatch(
                vAutoActionCreators.vAutoAppraisalSlideOutLoaded({
                    launchType,
                    tradeInIndex,
                    iframeLoadMs: performance.now() - shownAt
                })
            );
        }
    };

    // Listen for the vAuto save/finalize postMessage, then close. AppraisalSlideOut owns the refetch,
    // keyed off the close, so this doesn't need to fetch anything itself.
    useEffect(() => {
        let expectedOrigin = '';
        try {
            expectedOrigin = appraisalUrl ? new URL(appraisalUrl).origin : '';
        } catch {
            expectedOrigin = '';
        }

        if (!expectedOrigin) {
            return undefined;
        }

        const rejectedMessageReports: Record<VAutoAppraisalRejectionReason, number> = {
            originMismatch: 0,
            missingAppraisalId: 0
        };

        const reportRejected = (reason: VAutoAppraisalRejectionReason, receivedOrigin: string) => {
            if (rejectedMessageReports[reason] >= MAX_REJECTED_MESSAGE_REPORTS) {
                return;
            }
            rejectedMessageReports[reason]++;
            dispatch(vAutoActionCreators.vAutoAppraisalMessageRejected({ reason, receivedOrigin, expectedOrigin }));
        };

        const handleMessage = (event: MessageEvent) => {
            const isSavedNotice = event.data === VAUTO_APPRAISAL_SAVED_MESSAGE;
            const namesAppraisalAction = namesVAutoAppraisalAction(event.data);
            const isAppraisalPayload = isVAutoAppraisalMessage(event.data);

            // Only trust messages coming from the vAuto appraisal origin.
            if (event.origin !== expectedOrigin) {
                // Only for payloads shaped like ours — unrelated cross-frame chatter would swamp the event.
                if (isSavedNotice || namesAppraisalAction) {
                    reportRejected('originMismatch', event.origin);
                }
                return;
            }

            if (isSavedNotice) {
                // Only observable signal from the cross-origin iframe.
                dispatch(interactionAnalyticsActionCreators.appraisalSlidePanelCallbackReceived());
                dispatch(vAutoActionCreators.vAutoAppraisalMessageReceived({ messageType: 'saved' }));
            }

            if (!isAppraisalPayload) {
                // Trusted but incomplete: the panel stays open and the ACV refetch never runs. Behaviour
                // unchanged; reported so it is distinguishable from vAuto never replying.
                if (namesAppraisalAction) {
                    reportRejected('missingAppraisalId', event.origin);
                }
                return;
            }

            dispatch(
                vAutoActionCreators.vAutoAppraisalMessageReceived({
                    messageType: event.data.action === 'Finalize' ? 'finalize' : 'save'
                })
            );

            onClose();
        };
        const targetWindow = getPostMessageTarget();
        targetWindow.addEventListener('message', handleMessage);
        return () => targetWindow.removeEventListener('message', handleMessage);
    }, [appraisalUrl, onClose, dispatch]);

    if (!isVAutoAppraisalEnabled || !appraisalUrl) {
        return (
            <Box data-testid="appraisal-slideout-empty" sx={{ p: 2 }}>
                <Typography variant="body-md" tag="p">
                    {vin ? 'Appraisal is not available for this vehicle.' : 'Add a trade-in with a VIN to start an appraisal.'}
                </Typography>
            </Box>
        );
    }

    return (
        // SlideOutWithFixedFooter nests children in an auto-height container, so a
        // percentage height collapses. Size the iframe against the viewport to fill
        // the slide-out content region (which the framework sizes as 100% - 135px).
        <Box
            data-testid="appraisal-slideout-content"
            sx={{ position: 'relative', height: 'calc(100vh - 135px)', minHeight: '480px' }}
        >
            {!isIframeLoaded && (
                <Box
                    data-testid="appraisal-loader"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Spinner size={60} />
                </Box>
            )}
            <iframe
                data-testid="appraisal-iframe"
                title="vAuto Appraisal"
                src={appraisalUrl}
                onLoad={handleIframeLoad}
                style={{ border: 'none', width: '100%', height: '100%', display: 'block' }}
            />
        </Box>
    );
};
